import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Stars, StatusBadge } from './reviewParts';
import { formatDateTime } from '../../lib/dateUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyReviewsDialog = ({ open, onOpenChange, token }) => {
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [reviews, setReviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/reviews/mine`, { headers: { Authorization: `Bearer ${token}` } });
      setReviews(res.data);
    } catch {
      toast.error('Failed to load your reviews');
    }
  }, [token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const submit = async () => {
    if (rating < 1) return toast.error('Please choose a star rating.');
    if (message.trim().length < 3) return toast.error('Please write a short message.');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/reviews`, { rating, message: message.trim() }, { headers });
      toast.success('Thank you! Your review was sent for checking.');
      setRating(0);
      setMessage('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || 'Failed to send review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="my-reviews-dialog">
        <DialogHeader>
          <DialogTitle>Review NtwanaYam</DialogTitle>
          <DialogDescription>Tell us about your experience. Reviews are checked before they appear on the About Us page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Your rating</Label>
            <Stars value={rating} onChange={setRating} size="w-8 h-8" testId="review-star" />
          </div>
          <div>
            <Label>Your message</Label>
            <Textarea rows={3} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What do you like, or what can we do better?" data-testid="review-message-input" />
            <p className="text-xs text-muted-foreground text-right">{message.length}/500</p>
          </div>
          <Button onClick={submit} disabled={saving} className="w-full rounded-full" data-testid="review-submit-btn">
            {saving ? 'Sending...' : 'Send review'}
          </Button>
        </div>
        <div className="pt-2">
          <p className="text-sm font-semibold mb-2">My reviews</p>
          {reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="my-reviews-empty">You haven't written a review yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="my-reviews-list">
              {reviews.map((r) => (
                <li key={r.review_id} className="border rounded-xl p-3 space-y-1" data-testid={`my-review-${r.review_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <Stars value={r.rating} size="w-4 h-4" />
                    <StatusBadge status={r.status} testId={`my-review-status-${r.review_id}`} />
                  </div>
                  <p className="text-sm">{r.message}</p>
                  <p className="text-xs text-muted-foreground">Sent {formatDateTime(r.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
